import type {
  AssessTtqMultiSolutionWorkbenchRequest,
  AssessTtqMultiSolutionWorkbenchResponse,
  GetTtqWorkbenchContextResponse,
  ListTtqImageGenerationProvidersResponse,
  RunTtqCttqConvergeWorkbenchRequest,
  RunTtqMultiSolutionWorkbenchRequest,
  RunTtqQuestionChainWorkbenchRequest,
  RunTtqWorkbenchResponse,
  TtqaPathRun,
  TtqModelInvocationOptions,
} from '@kelilearning/psycheflow-contracts';

import { appConfig } from '../../config/appConfig';
import { apiClient } from '../../services/apiClient';
import { resolveApiErrorMessage } from '../../utils/authFormUtils';
import { TtqWorkbenchRequestError } from './ttqWorkbenchErrors';

export type TtqWorkbenchDerivedItem = {
  id: string;
  particleId: string;
  title?: string | null;
  question?: string;
  answer?: string;
  pathRunId?: string | null;
  createdAt?: string | null;
};

function ttqaBase(ttqaId: string) {
  return `/api/ttq/sessions/${encodeURIComponent(ttqaId)}`;
}

async function postWorkbench<T>(
  path: string,
  body: unknown,
  timeoutMs: number,
): Promise<T> {
  const payload = await apiClient.requestJson<{
    success?: boolean;
    data?: T;
    error?: string;
    errorCode?: string;
    details?: unknown;
    message?: string;
  }>(path, {
    method: 'POST',
    body,
    auth: true,
    timeoutMs,
  });
  if (!payload?.success || payload.data === undefined) {
    const message = resolveApiErrorMessage(payload, '品题工作台请求失败');
    throw new TtqWorkbenchRequestError(message, {
      errorCode: payload?.errorCode || (typeof payload?.error === 'string' ? payload.error : undefined),
      details: payload?.details ?? payload,
    });
  }
  return payload.data;
}

export const ttqWorkbenchService = {
  async getMultiSolutionContext(ttqaId: string, sourceParticleId: string) {
    const query = new URLSearchParams({ sourceParticleIds: sourceParticleId });
    return apiClient.getEnvelope<GetTtqWorkbenchContextResponse>(
      `${ttqaBase(ttqaId)}/workbench/multi_solution/context?${query.toString()}`,
    );
  },

  async getImageGenerationProviders(ttqaId: string) {
    return apiClient.getEnvelope<ListTtqImageGenerationProvidersResponse>(
      `${ttqaBase(ttqaId)}/image-generation-providers`,
    );
  },

  async assessMultiSolution(ttqaId: string, body: AssessTtqMultiSolutionWorkbenchRequest) {
    return postWorkbench<AssessTtqMultiSolutionWorkbenchResponse>(
      `${ttqaBase(ttqaId)}/workbench/multi_solution/assess`,
      body,
      appConfig.ttqWorkbenchAssessTimeoutMs,
    );
  },

  async runMultiSolution(ttqaId: string, body: RunTtqMultiSolutionWorkbenchRequest) {
    return postWorkbench<RunTtqWorkbenchResponse>(
      `${ttqaBase(ttqaId)}/workbench/multi_solution/run`,
      body,
      appConfig.ttqWorkbenchRunTimeoutMs,
    );
  },

  async getQuestionChainContext(ttqaId: string, sourceParticleId: string) {
    const query = new URLSearchParams({ sourceParticleIds: sourceParticleId });
    return apiClient.getEnvelope<GetTtqWorkbenchContextResponse>(
      `${ttqaBase(ttqaId)}/workbench/question_chain/context?${query.toString()}`,
    );
  },

  async getQuestionsChainContext(ttqaId: string, sourceParticleId: string) {
    const query = new URLSearchParams({ sourceParticleIds: sourceParticleId });
    return apiClient.getEnvelope<GetTtqWorkbenchContextResponse>(
      `${ttqaBase(ttqaId)}/workbench/questions_chain/context?${query.toString()}`,
    );
  },

  async runQuestionChain(ttqaId: string, body: RunTtqQuestionChainWorkbenchRequest) {
    return postWorkbench<RunTtqWorkbenchResponse>(
      `${ttqaBase(ttqaId)}/workbench/question_chain/run`,
      body,
      appConfig.ttqWorkbenchRunTimeoutMs,
    );
  },

  async previewQuestionChainDirection(ttqaId: string, body: RunTtqQuestionChainWorkbenchRequest) {
    return postWorkbench<{
      ttqaId: string;
      sourceParticleId: string;
      questionChainVariantId: string;
      variantLabel: string;
      directionPreview: string;
      modelAudit?: { provider?: string; model?: string };
      usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | null;
    }>(
      `${ttqaBase(ttqaId)}/workbench/question_chain/preview-direction`,
      body,
      appConfig.ttqWorkbenchAssessTimeoutMs,
    );
  },

  async runQuestionsChain(ttqaId: string, body: RunTtqQuestionChainWorkbenchRequest) {
    return postWorkbench<RunTtqWorkbenchResponse>(
      `${ttqaBase(ttqaId)}/workbench/questions_chain/run`,
      body,
      appConfig.ttqWorkbenchRunTimeoutMs,
    );
  },

  async previewCttqDirection(ttqaId: string, body: RunTtqCttqConvergeWorkbenchRequest) {
    return postWorkbench<{
      ttqaId: string;
      directionPreview: string;
      memberParticleIds: string[];
      modelAudit?: { provider?: string; model?: string; mode?: string };
      usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | null;
    }>(
      `${ttqaBase(ttqaId)}/workbench/cttq_converge/preview-direction`,
      body,
      appConfig.ttqWorkbenchAssessTimeoutMs,
    );
  },

  async assessPatternUnify(ttqaId: string, body: {
    sourceParticleIds: string[];
    customPrompt?: string;
    model?: TtqModelInvocationOptions;
  }) {
    return postWorkbench<{
      ttqaId: string;
      memberParticleIds: string[];
      canUnify: boolean;
      reasonMarkdown: string;
      promptPreview: string;
      directionPreview?: string;
      modelAudit?: { provider?: string; model?: string; mode?: string } | null;
    }>(
      `${ttqaBase(ttqaId)}/workbench/pattern_unify/assess`,
      body,
      appConfig.ttqWorkbenchAssessTimeoutMs,
    );
  },

  async runPatternUnify(ttqaId: string, body: {
    sourceParticleIds: string[];
    customPrompt?: string;
    directionPreview?: string;
    promptPreview?: string;
    generationPrompt?: string;
    model?: TtqModelInvocationOptions;
  }) {
    return postWorkbench<RunTtqWorkbenchResponse>(
      `${ttqaBase(ttqaId)}/workbench/pattern_unify/run`,
      body,
      appConfig.ttqWorkbenchRunTimeoutMs,
    );
  },

  async runCttqConverge(ttqaId: string, body: RunTtqCttqConvergeWorkbenchRequest) {
    return postWorkbench<RunTtqWorkbenchResponse>(
      `${ttqaBase(ttqaId)}/workbench/cttq_converge/run`,
      body,
      appConfig.ttqWorkbenchRunTimeoutMs,
    );
  },

  async listPathRuns(ttqaId: string) {
    return apiClient.getEnvelope<TtqaPathRun[]>(`${ttqaBase(ttqaId)}/path-runs`);
  },

  mapDerivedPreviews(context: GetTtqWorkbenchContextResponse): TtqWorkbenchDerivedItem[] {
    return (context.derivedPreviews || []).map((preview) => ({
      id: preview.particleId,
      particleId: preview.particleId,
      title: preview.title,
      pathRunId: preview.pathRunId,
      createdAt: preview.createdAt,
    }));
  },
};
